import { GOAL, MELODY } from './consts';
import { clamp, erf, mean, median, std } from './math';
import { freqToMidi, freqToNote, midiToFreq, semitoneClass } from './note';
import { vibratoAxisScore } from './vibrato';

import type {
  Metric,
  MetricKey,
  Metrics,
  MetricsInput,
  RangeKey,
  ToneKey,
  VoiceType,
} from './types';

/** Overall-score weights per axis. Sum to 1 when every axis is measured. */
const WEIGHTS: Record<MetricKey, number> = {
  pitchAccuracy: 0.3,
  pitchStability: 0.24,
  sustain: 0.16,
  resonance: 0.14,
  singersFormant: 0.1,
  vibrato: 0.06,
};

/**
 * The axes that were actually measured. An unmeasured axis carries a filler `val` (0)
 * next to `measured: false`; drawing, storing or comparing it reads as "scored zero".
 * Every consumer that shows or keeps axes filters through here, so what "unmeasured"
 * means lives in one place.
 */
export const measuredMetrics = (metrics: Metric[]): Metric[] =>
  metrics.filter((m) => m.measured !== false);

/**
 * Weighted average over the *measured* axes only, renormalising their weights.
 * Folding an unmeasured axis in as 0 would systematically depress the score —
 * a wrong number, not just an ugly one (the native path is missing two axes).
 */
const weightedOverall = (metrics: Metric[]): number => {
  const measured = measuredMetrics(metrics);
  const total = measured.reduce((sum, m) => sum + WEIGHTS[m.key], 0);
  if (total === 0) return 0;
  const weighted = measured.reduce((sum, m) => sum + m.val * WEIGHTS[m.key], 0);
  return clamp(Math.round(weighted / total));
};

/**
 * Each pitch as its distance from the nearest semitone, in cents (-50..50). A singer
 * holding a note that sits consistently sharp still reads as steady — what shows up
 * here is movement, not offset, because every reading is measured against its own
 * nearest semitone rather than a fixed target.
 */
export const centsOffNearestSemitone = (pitches: number[]): number[] =>
  pitches.map((f) => {
    const c = 1200 * Math.log2(f / 440);
    return c - Math.round(c / 100) * 100;
  });

/**
 * Steadiness of a held pitch, 0-100. The spread of the cents deviations is the
 * measurement; 150 cents of spread scores 0.
 *
 * Exported because the breath-stability segments score the same thing over shorter
 * windows (`segments.ts`), and a second copy of this scale would let a section score
 * 92 while the axis it rolls up into says 76.
 */
export const stabilityScore = (cents: number[]): number =>
  clamp(100 - std(cents) / 1.5);

/** Score → top-percentile (%), assuming a tester distribution (mean 56, sigma 17). */
export const scorePercentile = (score: number): number => {
  const z = (score - 56) / 17;
  const cdf = 0.5 * (1 + erf(z / Math.SQRT2));
  return Math.min(99, Math.max(1, Math.round((1 - cdf) * 100)));
};

/**
 * Voice-type keys from median pitch (range) + spectral centroid (brightness).
 * Returns language-neutral keys; apps compose a localized label via i18n.
 */
export const classifyVoice = (medF: number, centroid: number): VoiceType => {
  const rangeKey: RangeKey =
    medF < 140
      ? 'low'
      : medF < 200
        ? 'midLow'
        : medF < 280
          ? 'midHigh'
          : 'high';
  const toneKey: ToneKey =
    centroid < 1500 ? 'warm' : centroid < 2800 ? 'balanced' : 'bright';
  return {
    toneKey,
    rangeKey,
    hz: Math.round(medF),
    brightness: Math.round(centroid),
  };
};

export const computeMetrics = (input: MetricsInput): Metrics => {
  const { pitches, hnrs, centroids, sfs, onsetT, lastVoiceT, vibrato } = input;
  const medF = median(pitches);
  // 가장 가까운 반음(12음 중 아무 음)으로 스냅 — 특정 A음이 아니라 부른 음 기준.
  const targetF = midiToFreq(Math.round(freqToMidi(medF)));
  const note = freqToNote(medF);

  // 음정 정확도: 각 프레임을 '가장 가까운 반음'과 비교한 편차(cents, [-50,50]).
  // 멜로디(여러 음)를 불러도 음마다 자기 음 기준이라 의미 있는 값이 나온다.
  const cents = centsOffNearestSemitone(pitches);
  const meanAbsCents = mean(cents.map(Math.abs));
  const centsStd = std(cents);

  const pitchAccuracy = clamp(100 - meanAbsCents * 2); // 50센트 어긋남→0, 정확→100
  const pitchStability = stabilityScore(cents);
  const voicedSec =
    onsetT != null && lastVoiceT != null
      ? (lastVoiceT - onsetT) / 1000
      : pitches.length * 0.016;
  const sustain = clamp((voicedSec / GOAL) * 100);
  // 네이티브 캡처 경로는 스펙트럼을 넘기지 않는다(raw PCM 금지) → 빈 배열이 온다.
  // mean([]) = NaN 이므로 여기서 막지 않으면 resonance→overall→percentile이 전부 NaN이 된다.
  // 세 입력은 서로 독립이다 — 각 소비자를 자기가 실제로 쓰는 배열에만 묶는다.
  const hasHnr = hnrs.length > 0;
  const hasCentroids = centroids.length > 0;
  const hasSingersFormant = sfs.length > 0;
  const centroidMean = hasCentroids ? mean(centroids) : 0;
  const hnrMean = hasHnr ? mean(hnrs) : 0;
  const centroidCV = hasCentroids ? std(centroids) / (centroidMean || 1) : 0;
  // resonance는 두 배열을 다 쓴다 → 하나라도 없으면 미측정.
  const hasResonance = hasHnr && hasCentroids;
  const resonance = clamp(hnrMean * 4 + (100 - centroidCV * 120) * 0.4);
  const sfMean = hasSingersFormant ? mean(sfs) : 0;
  const singersFormant = clamp(sfMean * 600);
  const voiceType = classifyVoice(medF, centroidMean);

  const metrics: Metric[] = [
    {
      key: 'pitchAccuracy',
      val: Math.round(pitchAccuracy),
      descParams: { cents: meanAbsCents.toFixed(0) },
    },
    {
      key: 'pitchStability',
      val: Math.round(pitchStability),
      descParams: { cents: centsStd.toFixed(0) },
    },
    {
      key: 'sustain',
      val: Math.round(sustain),
      descParams: { sec: voicedSec.toFixed(1) },
    },
    {
      key: 'vibrato',
      val: vibrato ? Math.round(vibratoAxisScore(vibrato)) : 0,
      measured: vibrato != null,
    },
    { key: 'resonance', val: Math.round(resonance), measured: hasResonance },
    {
      key: 'singersFormant',
      val: Math.round(singersFormant),
      measured: hasSingersFormant,
    },
  ];
  const overall = weightedOverall(metrics);
  return {
    metrics,
    overall,
    percentile: scorePercentile(overall),
    note,
    medF: Math.round(medF),
    targetF: Math.round(targetF),
    meanAbsCents,
    sustainSec: voicedSec,
    singersFormant: Math.round(singersFormant),
    voiceType,
    range: null,
  };
};

export const scoreSong = (pitches: number[]): number | null => {
  if (!pitches || pitches.length < 8) return null;
  const target = [...new Set(MELODY.map(semitoneClass))];
  const got = new Set(pitches.filter((f) => f > 0).map(semitoneClass));
  let hit = 0;
  target.forEach((c) => {
    if (got.has(c)) hit++;
  });
  return Math.round((hit / target.length) * 100);
};
